import { Component, Input, OnInit } from "@angular/core";
import { SharedDsnModule } from "@app/shared/modules/shared-dsn.module";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import { VisualizationOptionsType } from "@arch/_shared/types/options-visualization";
import {
    CompanyEntity,
    PointEntity,
    SerieEntity,
    SerieType,
    VisualizationEntity
} from "@arch/domain/entity";
import { HighchartsRenderComponent } from "../../highcharts-render/highcharts-render.component";
import { FormatNumbers } from "@arch/_shared/utils/format-number.utils";

@Component({
    selector: "app-table-management-assets",
    imports: [HighchartsRenderComponent, SharedMaterialModule, SharedDsnModule],
    templateUrl: "./table-management-assets.component.html",
    styleUrl: "./table-management-assets.component.scss"
})
export class TableManagementAssetsComponent implements OnInit {
    @Input({ required: true }) visualization: VisualizationEntity;
    @Input({ required: true }) options: VisualizationOptionsType;
    @Input({ required: true }) series: SerieEntity[];
    @Input({ required: true }) points: PointEntity[];
    @Input({ required: true }) company: CompanyEntity;

    public seriesData: SerieEntity[] = [];
    public pointsData: PointEntity[] = [];
    public isExpanded: { [key: string]: boolean } = {};
    tableData: TableData[] = [];

    public onToggle(status: boolean) {}

    ngOnInit(): void {
        this.mountTable();
        this.setVisualization();
    }

    private setVisualization(): void {
        if (!this.series.length) return;
        const mainSerieId =
            this.visualization.props.component?.configs?.chart?.series?.[0]
                ?._id;
        const seriesData = this.series.filter(
            (s) => s.props._id === mainSerieId
        );
        const pointsData = this.points.filter((p) => p.props.s === mainSerieId);
        if (
            this.visualization.props.goals &&
            this.visualization.props.goals.length
        ) {
            const mainGoals = this.visualization.props.goals.find(
                (goal) => goal.s === mainSerieId
            );
            this.visualization.props.component.configs.chart.goals = [
                {
                    type: "line",
                    _id: mainGoals.r,
                    color: "cyan.dark"
                }
            ];
            const serie = this.series.find(
                (s) => s.props.referenceId === mainGoals.r
            );
            if (serie) {
                seriesData.push(serie);
            }
            if (pointsData.length) {
                pointsData.push(
                    ...this.points.filter(
                        (p) => p.props.s === serie?.props?._id
                    )
                );
            }
        }
        this.seriesData = seriesData;
        this.pointsData = pointsData;
        this.visualization.props.component.configs.chart.series =
            this.visualization.props.component.configs.chart.series.map(
                (s: SerieType) => ({ type: "column", ...s })
            );
        this.visualization.setChartConfiguration(
            this.visualization.props.component.configs.chart
        );
    }

    private getLastPointValue(serieId: string): string {
        const lastValue = this.points
            .filter((p) => p.props.s === serieId)
            .slice(-1)[0];
        return FormatNumbers.byUnit("currency", lastValue?.props?.m?.v);
    }

    private getLastComparationPointValue(serieId: string): string {
        const lastValue = this.points
            .filter(
                (p) =>
                    p.props.s === serieId &&
                    +p.t === +this.options.comparation.date
            )
            .slice(-1)[0];
        return FormatNumbers.byUnit("currency", lastValue?.props?.m?.v);
    }

    private mountTable(): void {
        const calculateds = [
            { name: "Imobilizado", value: "40%" },
            { name: "Imobilizado Liquido", value: "40%" },
            { name: "Estoque", value: "30%" }
        ];
        const configs = this.visualization.props.component.configs;
        const tableData: TableData[] = [];
        for (let index in configs.table) {
            const table = configs.table[index];
            this.isExpanded[index] = true;
            const tableRows = table.rows
                .filter((r: string) => r.length > 0)
                .map((serieId: string) => {
                    const serie = this.series.find(
                        (s) => s.props._id === serieId
                    );
                    const calculated = calculateds.find(
                        (c) => c.name === serie?.props?.name
                    );
                    const value = this.options.comparation.active
                        ? this.getLastComparationPointValue(serieId)
                        : this.getLastPointValue(serieId);
                    return {
                        label: serie?.props?.name || "",
                        value,
                        percentage: calculated?.value || null
                    };
                });

            const total = this.options.comparation.active
                ? this.getLastComparationPointValue(table.total)
                : this.getLastPointValue(table.total);

            tableData.push({
                title: table.thead,
                rows: tableRows,
                total
            });
        }
        this.tableData = tableData;
    }
}

export interface ITableManagementAssetsComponent {
    series: SerieEntity[];
    points: PointEntity[];
    visualization: VisualizationEntity;
    options: VisualizationOptionsType;
    company?: CompanyEntity;
}

export type TableData = {
    title: string;
    rows: { label: string; value: string; percentage?: string }[];
    total: string;
};
